/**
 * World 1: First Spark ⚡ (ages 6+). A circuit is a loop.
 *
 * Shared layout on a 7×5 board:
 *
 *    x: 0 1 2 3 4 5 6
 *  y0   . . . . . . .
 *  y1   . 🔋━━━━━━━ ◉      battery (1,1)→(1,4) vertical, "+" at top
 *  y2   . ┃       ┃ .      part on the right (5,1)→(5,3)
 *  y3   . ┃       ◉ .      link wire (5,3)→(5,4)
 *  y4   . ━━━━━━━━━ .      bottom wire (1,4)→(5,4)
 */
import type { Level, PresetPart } from './types';

const BOARD = { cols: 7, rows: 5 };
const battery: PresetPart = { type: 'battery', x: 1, y: 1, rot: 1 };
const topWire: PresetPart = { type: 'wire', x: 1, y: 1, rot: 0, props: { length: 5, color: 'purple' } };
const bottomWire: PresetPart = { type: 'wire', x: 1, y: 4, rot: 0, props: { length: 5, color: 'purple' } };
const link: PresetPart = { type: 'wire', x: 5, y: 3, rot: 1, props: { length: 2, color: 'blue' } };
const rightPart = (type: PresetPart['type'], props = {}): PresetPart => ({ type, x: 5, y: 1, rot: 1, props });

export const WORLD1: Level[] = [
  {
    id: 'w1-1',
    world: 1,
    num: 1,
    title: 'Close the Loop',
    guide: true,
    emoji: '🔌',
    intro:
      'The bulb wants to glow, but there is a gap! Put your finger on a silver snap and draw a wire to the other snap.',
    board: BOARD,
    preset: [
      { ...battery, locked: true },
      { ...rightPart('lamp'), locked: true },
      { ...topWire, locked: true },
      { ...link, locked: true },
    ],
    tray: [{ type: 'wire', props: { color: 'purple' }, count: 1 }],
    steps: [{ text: 'Light up the bulb', when: { lit: 'lamp' } }],
    hints: [
      'Electricity needs a full circle to travel. Find the gap at the bottom!',
      'Touch the silver snap at the bottom of the battery, and slide your finger along the glowing outline.',
      'Draw from the bottom battery snap all the way to the bottom snap on the right. Then let go!',
    ],
    solution: [bottomWire],
    outro:
      'You made a circuit! A circuit is a loop. Electricity leaves the battery, goes through the bulb, and comes home again.',
  },
  {
    id: 'w1-2',
    world: 1,
    num: 2,
    title: 'Light It Up',
    guide: true,
    emoji: '💡',
    intro: 'Now there are two gaps! Draw two wires to connect the battery to the bulb.',
    board: BOARD,
    preset: [
      { ...battery, locked: true },
      { ...rightPart('lamp'), locked: true },
      { ...link, locked: true },
    ],
    tray: [{ type: 'wire', props: { color: 'purple' }, count: 2 }],
    steps: [{ text: 'Light up the bulb', when: { lit: 'lamp' } }],
    hints: [
      'One wire goes on top, and one wire goes on the bottom.',
      'Follow the glowing outlines with your finger, from snap to snap.',
      'Start on a silver snap, slide to another silver snap, and let go.',
    ],
    solution: [topWire, bottomWire],
    outro:
      'Did you know? Electricity travels super fast, almost as fast as light. It goes around your loop in a blink!',
  },
  {
    id: 'w1-3',
    world: 1,
    num: 3,
    title: 'Build It Yourself',
    emoji: '🛠️',
    intro: 'This time the board is empty. Can you build the whole loop? Battery, bulb, and wires!',
    board: BOARD,
    preset: [],
    tray: [
      { type: 'battery', count: 1 },
      { type: 'lamp', count: 1 },
      { type: 'wire', props: { color: 'purple' }, count: 3 },
    ],
    steps: [{ text: 'Light up the bulb', when: { lit: 'lamp' } }],
    hints: [
      'Start with the battery. Then add the bulb, then draw wires from snap to snap.',
      'The glowing outline shows where the next part can go.',
      'Every part has two snaps. Each snap must touch another snap to make a loop.',
    ],
    solution: [battery, rightPart('lamp'), topWire, link, bottomWire],
    outro:
      'You are a real builder now! Engineers plan the loop first, then snap the parts together, just like you.',
  },
  {
    id: 'w1-4',
    world: 1,
    num: 4,
    title: 'Light Switch',
    emoji: '🎚️',
    intro: 'Let’s add a switch, so you can turn the light on and off whenever you want!',
    board: { cols: 7, rows: 6 },
    preset: [
      { type: 'battery', x: 1, y: 1, rot: 1, locked: true },
      { type: 'lamp', x: 5, y: 2, rot: 1, locked: true },
      { type: 'wire', x: 1, y: 4, rot: 0, props: { length: 5, color: 'purple' }, locked: true },
    ],
    tray: [
      { type: 'switch', count: 1 },
      { type: 'wire', props: { color: 'purple' }, count: 4 },
    ],
    steps: [
      {
        text: 'Turn the bulb ON with the switch',
        when: { all: [{ has: 'switch' }, { switch: 'on' }, { lit: 'lamp' }] },
      },
      { text: 'Now turn it OFF', when: { all: [{ switch: 'off' }, { dark: 'lamp' }] } },
    ],
    hints: [
      'The switch is a gate in the loop. Put it on the top road between the battery and the bulb.',
      'Look for the glowing outlines. Tap the switch to flip it!',
      'Tap the switch to slide it to ON. Tap it again to turn it OFF.',
    ],
    solution: [
      { type: 'switch', x: 2, y: 1, rot: 0 },
      { type: 'wire', x: 1, y: 1, rot: 0, props: { length: 2, color: 'blue' } },
      { type: 'wire', x: 4, y: 1, rot: 0, props: { length: 2, color: 'blue' } },
      { type: 'wire', x: 5, y: 1, rot: 1, props: { length: 2, color: 'blue' } },
    ],
    outro:
      'The light switch on your wall works just like this one. It opens and closes a gate for the electricity!',
  },
  {
    id: 'w1-5',
    world: 1,
    num: 5,
    title: 'Fan Time',
    emoji: '🌀',
    intro: 'Motors turn electricity into spinning! Put the motor in the loop and watch the fan go.',
    board: BOARD,
    preset: [
      { ...battery, locked: true },
      { ...topWire, locked: true },
      { ...link, locked: true },
      { ...bottomWire, locked: true },
    ],
    tray: [{ type: 'motor', count: 1 }],
    steps: [{ text: 'Make the fan spin', when: { spinning: true } }],
    hints: [
      'There is a space on the right side of the loop, just the right size for the motor.',
      'Drag the motor onto the glowing outline.',
      'The motor needs to go from the top-right snap down to the blue wire.',
    ],
    solution: [rightPart('motor')],
    outro: 'Motors are everywhere: in fans, washing machines, toy cars, and even electric toothbrushes!',
  },
  {
    id: 'w1-6',
    world: 1,
    num: 6,
    title: 'Doorbell',
    emoji: '🔔',
    intro: 'Ding dong! Build a doorbell. It should only ring while someone presses the button.',
    board: { cols: 7, rows: 6 },
    preset: [
      { type: 'battery', x: 1, y: 1, rot: 1, locked: true },
      { type: 'wire', x: 1, y: 4, rot: 0, props: { length: 5, color: 'purple' }, locked: true },
    ],
    tray: [
      { type: 'button', count: 1 },
      { type: 'buzzer', count: 1 },
      { type: 'wire', props: { color: 'purple' }, count: 3 },
    ],
    steps: [
      { text: 'Press the button to ring the bell', when: { all: [{ pressed: true }, { buzzing: true }] } },
      { text: 'Let go: the bell stops', when: { all: [{ has: 'button' }, { quiet: true }] } },
    ],
    hints: [
      'The button goes on the top road. The buzzer goes on the right side.',
      'Follow the glowing outlines!',
      'Hold your finger on the red button to ring the bell.',
    ],
    solution: [
      { type: 'button', x: 2, y: 1, rot: 0 },
      { type: 'buzzer', x: 5, y: 2, rot: 1 },
      { type: 'wire', x: 1, y: 1, rot: 0, props: { length: 2, color: 'blue' } },
      { type: 'wire', x: 4, y: 1, rot: 0, props: { length: 2, color: 'blue' } },
      { type: 'wire', x: 5, y: 1, rot: 1, props: { length: 2, color: 'blue' } },
    ],
    outro:
      'A press button has a spring inside. When you let go, the spring pops the gate open and the sound stops.',
  },
  {
    id: 'w1-7',
    world: 1,
    num: 7,
    title: 'Flying Saucer!',
    emoji: '🛸',
    intro: 'Let’s make the fan FLY! Spin the fan up fast with the switch... then switch it off and watch!',
    board: { cols: 7, rows: 6 },
    preset: [
      { type: 'battery', x: 1, y: 1, rot: 1, locked: true },
      { type: 'wire', x: 1, y: 4, rot: 0, props: { length: 5, color: 'purple' }, locked: true },
      { type: 'wire', x: 1, y: 1, rot: 0, props: { length: 2, color: 'blue' }, locked: true },
      { type: 'wire', x: 4, y: 1, rot: 0, props: { length: 2, color: 'blue' }, locked: true },
      { type: 'wire', x: 5, y: 1, rot: 1, props: { length: 2, color: 'blue' }, locked: true },
    ],
    tray: [
      { type: 'switch', count: 1 },
      { type: 'motor', count: 1 },
    ],
    steps: [
      {
        text: 'Spin the fan up with the switch',
        when: { all: [{ switch: 'on' }, { spinning: true, min: 30 }] },
      },
      { text: 'Switch it OFF to launch!', when: { event: 'launch' } },
    ],
    hints: [
      'Put the switch on the top road and the motor on the right side.',
      'Turn the switch ON and wait until the fan is spinning really fast.',
      'Now tap the switch OFF. Up, up, and away!',
    ],
    solution: [
      { type: 'switch', x: 2, y: 1, rot: 0 },
      { type: 'motor', x: 5, y: 2, rot: 1 },
    ],
    outro:
      'The fan blades are tilted, so they push air down when they spin. When the motor stops, the spinning fan keeps pushing and flies up, just like a helicopter!',
  },
];
